import React, { ReactNode } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { FileUploadButton } from './FileUploadButton';
import { Stack } from '../stack';
import { IconCircleXFill, IconLoadingDonuts, IconCircleWarning } from '../icons/generated';
import { semantic_colors, colors, shapes, text_styles } from '../../foundation';
import { BaseText } from '../text';
import { ButtonProps } from '../button';
import { getAutoColorCode } from '../../styles';

export type UploadFileStatus = 'error' | 'success' | 'done' | 'uploading';
// eslint-disable-next-line
export type FileType = File & { [key: string]: any };

export interface UploadFile {
  value: FileType | null;
  status?: UploadFileStatus;
  /**
   * 현재 업로드 진행률 (0-100 사이의 값)
   */
  percent?: number;
  url?: string;
}

export interface FileUploadProps {
  className?: string;
  label?: ReactNode;
  /** @default 'vertical' */
  direction?: 'vertical' | 'horizontal';
  /** @default 'image/*' */
  accept?: HTMLInputElement['accept'];
  disabled?: boolean;
  loading?: boolean;
  multiple?: boolean;
  /**
   * 최대 업로드 파일 갯수
   */
  maxCount?: number;
  /**
   * FileList내 삭제버튼 사용 유무
   * @default true
   */
  removable?: boolean;
  files?: UploadFile[];

  buttonProps?: Omit<ButtonProps, 'onClick' | 'disabled'>;

  onChange?: (file: File | File[]) => void;
  /**
   * 파일 업로드 버튼 클릭시 실행되는 이벤트
   */
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  /**
   * FileList 클릭시 인덱스 정보와 함께 전달합니다.
   */
  onFileClick?: (file: UploadFile, index: number) => void;
  /**
   * 삭제 버튼 클릭시 인덱스 정보와 함께  전달합니다.
   */
  onFileRemove?: (file: UploadFile, index: number) => void;
}

function getFileExtension(filename: string): {
  extension: string;
  file_name: string;
} {
  const index = filename.lastIndexOf('.');
  if (index > 0) {
    return {
      extension: filename.substring(index + 1),
      file_name: filename.substring(0, index),
    };
  }
  return {
    extension: '',
    file_name: filename,
  };
}

// TODO(@olive-sally): progress bar 컴포넌트 분리(공용 디자인 요청)
export const FileUpload = ({
  className,
  accept = 'image/*',
  direction = 'vertical',
  label,
  disabled,
  loading,
  multiple,
  maxCount,
  removable = true,
  files = [],
  buttonProps,
  onChange,
  onClick,
  onFileClick,
  onFileRemove,
}: FileUploadProps) => {
  const is_vertical = direction === 'vertical';

  return (
    <Stack
      direction={is_vertical ? 'column' : 'row'}
      align='flex-start'
      className={classNames('pds-file-upload', className)}
      gap={4}
    >
      <FileUploadButton
        label={label}
        accept={accept}
        disabled={Boolean(disabled || (maxCount && maxCount <= files.length))}
        multiple={multiple}
        className={className}
        onChange={onChange}
        loading={loading}
        buttonProps={buttonProps}
        onClick={onClick}
      />
      {files.length > 0 && (
        <FileListContainer
          width={is_vertical ? '100%' : undefined}
          direction={is_vertical ? 'column' : 'row'}
          gap={4}
          align='flex-start'
        >
          {files.map((file, index) => {
            const fileError = file?.status === 'error';
            const disabledColor = fileError
              ? semantic_colors.state.negative_disabled
              : semantic_colors.content.disabled;
            const { file_name, extension } = getFileExtension(file.value?.name || file.url || '');

            return (
              <FileUploadListItem
                className='pds-file-upload-list-item'
                gap={8}
                align='center'
                disabled={Boolean(disabled)}
                key={index}
                clickEvent={!!onFileClick}
                onClick={() => onFileClick?.(file, index)}
              >
                {file.status === 'uploading' && !disabled && <IconLoadingDonuts size={16} color={colors.gray250} />}
                {file.status === 'error' && (
                  <IconCircleWarning size={16} color={disabled ? disabledColor : semantic_colors.state.negative} />
                )}
                <FileNameContainer
                  color={
                    disabled
                      ? disabledColor
                      : fileError
                      ? semantic_colors.state.negative
                      : semantic_colors.content.secondary
                  }
                >
                  <FilenameTag typography='Body_12_Regular'>{file_name}.</FilenameTag>
                  <BaseText typography='Body_12_Regular'>{extension}</BaseText>
                </FileNameContainer>
                {removable && (
                  <RemoveButton
                    type='button'
                    disabled={disabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      onFileRemove?.(file, index);
                    }}
                    // @ts-expect-error: TS2322
                    state={file.status}
                  >
                    <IconCircleXFill size={16} color={disabled ? disabledColor : 'currentColor'} />
                  </RemoveButton>
                )}
                {file?.percent && !disabled && (
                  <ProgressWrapper>
                    <LinearProgress
                      style={{
                        transform: `translateX(${Number(((file.percent / 100) * 100).toFixed(0)) - 100}%)`,
                      }}
                    />
                  </ProgressWrapper>
                )}
              </FileUploadListItem>
            );
          })}
        </FileListContainer>
      )}
    </Stack>
  );
};

const FileUploadListItem = styled(Stack)<{ disabled: boolean; clickEvent: boolean }>`
  position: relative;
  height: 28px;
  padding: 4px 8px;
  max-width: 100%;
  background-color: ${semantic_colors.fill.tertiary};
  ${text_styles.Body_12_Regular};
  ${shapes.border_radius.small};
  transition: all 0.1s linear;

  ${({ clickEvent, disabled }) => {
    return (
      clickEvent &&
      `
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        &:hover {
          background-color: ${disabled ? colors.gray10 : colors.gray20};
        }
        &:active {
          background-color: ${disabled ? colors.gray10 : colors.gray30};
        }
    `
    );
  }};
`;
const FileListContainer = styled(Stack)`
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
`;

const FileNameContainer = styled(Stack)<{ color: string }>`
  min-width: 0;
  flex: 1;
  color: ${({ color }) => color};
`;

const FilenameTag = styled(BaseText)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button<{ state: UploadFileStatus }>`
  margin-left: auto;
  padding: 0;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  border: 1px solid transparent;
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  ${({ state }) =>
    state === 'error'
      ? `
        color: ${semantic_colors.state.negative};
        &:hover {
          color: ${getAutoColorCode(semantic_colors.state.negative).hover};
        }
        &:active {
          color: ${getAutoColorCode(semantic_colors.state.negative).pressed};
        }
      `
      : `
        color: ${colors.gray400};
        &:hover {
          color: ${colors.gray550};
        }
        &:active {
          color: ${colors.gray600};
        }
    `}
`;

const ProgressWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 1px;
  overflow: hidden;
`;

const LinearProgress = styled.div`
  height: 1px;
  background-color: ${semantic_colors.accent.primary};
  transition: transform 500ms ease-out;
`;
